/**
 * Learn — What is a time-distance diagram (time-space diagram) for traffic signals?
 * Framing V10 page 1. Target queries: "what is a time-distance diagram",
 * "time-space diagram traffic signals".
 */

import Link from '../Link';
import LearnLayout from './LearnLayout';
import TimeDistanceFigure from './TimeDistanceFigure';
import {
  EXAMPLE_CORRIDOR,
  bandwidth,
  corridorLength,
  cycleLength,
  fmt,
  greenWindows,
} from './corridor-example';
import type { PublicRoute } from '../../lib/route';

export default function TimeDistanceDiagramPage({ route }: { route: PublicRoute }) {
  const cycle = cycleLength(EXAMPLE_CORRIDOR[0]);
  const nbBand = bandwidth(EXAMPLE_CORRIDOR, 'N');
  const nbGreen = greenWindows(EXAMPLE_CORRIDOR[0], 'N')[0];
  const nbGreenLength = nbGreen ? nbGreen.end - nbGreen.start : 0;

  return (
    <LearnLayout
      route={route}
      heading="What is a time-distance diagram (time-space diagram) for traffic signals?"
      lede="A time-distance diagram shows, on one drawing, when every traffic signal along a corridor is green and where a vehicle travelling at the design speed would be at each moment. It is the working drawing of signal coordination: if the sloped line of a vehicle passes through green at every intersection, the corridor has a green wave."
    >
      <h2 id="two-names">Two names, one drawing</h2>
      <p>
        <strong>Time-distance diagram</strong> is the usual term in Australia, New Zealand and the United
        Kingdom; <strong>time-space diagram</strong> is the North American term used in the ITE and FHWA
        signal timing literature. Both describe the same plot: distance along the corridor on one axis,
        time on the other. Construction schedulers use the same words for a different chart (a linear
        schedule of chainage against date), which is why searching the bare phrase turns up scaffolding
        and railway timetables; the traffic-signal version is the one on this page.
      </p>

      <h2 id="axes">The axes</h2>
      <p>
        SigTDD, like the desktop tools before it, puts <strong>distance</strong> up the vertical axis in
        metres from the first intersection and <strong>time</strong> across the horizontal axis in seconds.
        Each signalised intersection sits at its cumulative distance from the start of the corridor, so a
        450 m link is drawn one and a half times as tall as a 300 m link. Time starts at an arbitrary
        reference, normally the start of the first intersection's cycle, and repeats every cycle length.
      </p>

      <figure className="learn-figure">
        <TimeDistanceFigure
          corridor={EXAMPLE_CORRIDOR}
          title="Time-distance diagram of the Riverside Drive example corridor with a northbound green wave"
          description={`Six intersections from 0 to ${corridorLength(EXAMPLE_CORRIDOR)} metres, a ${cycle}-second cycle, phase bars at each intersection shifted by their offsets, and a translucent northbound band ${fmt(nbBand)} seconds wide that meets green at every intersection.`}
        />
        <figcaption>
          The six-intersection Riverside Drive example that ships with SigTDD (NB-Green-Wave.csv), drawn
          from its CSV values. The blue band is the set of northbound departures from Oak Street that meet
          green at every signal north of it.
        </figcaption>
      </figure>

      <h2 id="phase-bar">Phase bars</h2>
      <p>
        At each intersection's distance a horizontal bar shows the signal sequence in time: phase A, then
        B, then C and so on, each ending in amber, then the sequence repeats. The bar is shifted right by
        the intersection's <strong>offset</strong>, the number of seconds between the corridor reference and
        the start of that intersection's cycle. Colour says which through movement each phase serves:
      </p>
      <ul>
        <li><strong>Cyan</strong> — both northbound and southbound through movements run (an "NS" or "NBSB" phase).</li>
        <li><strong>Purple</strong> — northbound only, typically a leading or lagging northbound turn phase that also carries the through.</li>
        <li><strong>Green</strong> — southbound only.</li>
        <li><strong>Grey</strong> — a phase outside the corridor progression: a cross-street phase, a pedestrian phase, a protected turn that stops the through.</li>
        <li><strong>Amber</strong> — the last seconds of each green run, deducted from usable green.</li>
      </ul>
      <p>
        The <strong>cycle length</strong> is the sum of the phase durations. In the example every
        intersection runs a {cycle}-second cycle: 40 s both directions, 22 s northbound, 22 s southbound,
        18 s cross street, 18 s both directions. A corridor can only be coordinated when every signal runs
        the same cycle length, or an exact multiple of it, because otherwise the offsets drift apart cycle
        by cycle.
      </p>

      <h2 id="trajectory">Vehicle trajectories</h2>
      <p>
        A vehicle moving along the corridor at a steady speed traces a straight sloped line: it covers
        distance up the page at a rate set by its speed. At 60 km/h it covers 16.7 m every second, so a
        400 m link takes 24 s and the line rises 400 m while moving 24 s to the right. A faster vehicle
        draws a shallower line, a slower one a steeper line, and a stopped vehicle draws a horizontal line.
        Northbound lines rise to the right; southbound lines fall to the right.
      </p>
      <p>
        Where a trajectory crosses a phase bar the colour under it tells you what the driver sees: green
        for a movement that includes their direction, or red. Reading the diagram is nothing more than
        following a line from the first signal to the last and checking what it crosses.
      </p>

      <h2 id="green-wave">Green wave and bandwidth</h2>
      <p>
        Draw the trajectory that leaves the first intersection at the very start of its northbound green,
        and the one that leaves at the very end. If both lines, and every line between them, cross green at
        every downstream signal, the set of departure times between them is the <strong>green wave</strong>,
        also called the <strong>progression band</strong> or <strong>through band</strong>. Its width in seconds is
        the <strong>bandwidth</strong>; divided by the cycle length it gives the <strong>efficiency</strong>.
      </p>
      <p>
        In the example above the northbound green at Oak Street is {fmt(nbGreenLength)} s (phases A and B
        together, less 4 s of amber), and because every offset equals the cumulative travel time at 60 km/h,
        the full {fmt(nbBand)} s reaches Maple Court unbroken: an efficiency of{' '}
        {fmt((nbBand / cycle) * 100)} percent. SigTDD reports the bandwidth for each direction in the
        corridor header and shades the band on the canvas as the <em>Sequence</em> display option.
      </p>
      <p>
        A second band, <em>Next Green</em>, shows only the departures that arrive on green at the very next
        intersection rather than all of them; it is the tool for adjusting one pair of signals at a time.
        Dashed <em>Phase Extent</em> lines project the start and end of each green at the design speed, so
        you can see exactly which phase at the next signal a platoon will meet.
      </p>

      <h2 id="use">What engineers use the diagram for</h2>
      <ul>
        <li>
          <strong>Setting offsets.</strong> Slide an intersection's phase bar left or right until the band
          passes through. On paper this took a ruler and a fresh sheet per attempt; on screen the band
          redraws as the slider moves. The arithmetic is on the{' '}
          <Link to="/learn/green-wave-offset" event="learn-open" source="learn">green wave offset</Link> page.
        </li>
        <li>
          <strong>Choosing a direction to favour.</strong> Two-way progression is only perfect when the
          spacing suits the cycle length; the diagram makes the trade-off visible, so the AM plan can favour
          inbound traffic and the PM plan outbound.
        </li>
        <li>
          <strong>Checking a design speed.</strong> Tilting the trajectory shows whether a platoon at the
          posted speed, or at the speed drivers actually use, still fits the band.
        </li>
        <li>
          <strong>Explaining a plan.</strong> A council committee or a client can see why a cycle length was
          chosen from one picture where a table of offsets says nothing.
        </li>
      </ul>

      <h2 id="build">Building one in SigTDD</h2>
      <p>
        The intersection table needs, per intersection: the distance to the next signal, the northbound
        and southbound design speeds, the offset and the phase durations with their directions. Type them,
        or import a CSV, a SigTDD JSON export or a Synchro UTDF file, and the diagram is drawn. The{' '}
        <Link to="/learn/worked-example-corridor" event="learn-open" source="learn">worked example</Link>{' '}
        goes through every number for the corridor drawn above.
      </p>
    </LearnLayout>
  );
}
